import React, { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const getStatusBadge = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-800',
    suspended: 'bg-amber-100 text-amber-800',
    inactive: 'bg-muted text-muted-foreground',
  };
  return map[status] || 'bg-muted text-muted-foreground';
};

const getStatusText = (status) => {
  const map = { active: 'Ativo', suspended: 'Suspenso', inactive: 'Inativo' };
  return map[status] || status;
};

export default function PartnerManagement() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    nif: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    commission_percentage: '10.00',
    status: 'active',
  });

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const res = await api.get('/partners', { params });
      setPartners(res.data?.data || []);
    } catch {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, search]);

  const resetForm = () => {
    setFormData({
      company_name: '',
      nif: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      commission_percentage: '10.00',
      status: 'active',
    });
    setEditing(null);
  };

  const openEdit = (partner) => {
    setEditing(partner);
    setFormData({
      company_name: partner.company_name,
      nif: partner.nif,
      contact_name: partner.contact_name || '',
      contact_email: partner.contact_email,
      contact_phone: partner.contact_phone || '',
      commission_percentage: partner.commission_percentage,
      status: partner.status,
    });
    setShowModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy('save');
    try {
      if (editing) {
        await api.put(`/partners/${editing.id}`, formData);
        toast.success('Parceiro atualizado');
      } else {
        await api.post('/partners', formData);
        toast.success('Parceiro criado');
      }
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao guardar parceiro');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deseja excluir este parceiro?')) return;
    setBusy(id);
    try {
      await api.delete(`/partners/${id}`);
      toast.success('Parceiro excluído');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir parceiro');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Parceiros...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Parceiros</h1>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <Plus size={16} className="mr-2" /> Novo Parceiro
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium">Pesquisar</label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome, NIF ou email..."
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Todos</option>
                <option value="active">Ativo</option>
                <option value="suspended">Suspenso</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setFilterStatus('');
                  setSearch('');
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Parceiros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Empresa</th>
                  <th>NIF</th>
                  <th>Contacto</th>
                  <th>Email</th>
                  <th className="text-right">Comissão</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="py-2 font-medium">{p.company_name}</td>
                    <td>{p.nif}</td>
                    <td>{p.contact_name || '—'}</td>
                    <td>{p.contact_email}</td>
                    <td className="text-right">{p.commission_percentage}%</td>
                    <td>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(p.status)}`}>
                        {getStatusText(p.status)}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                          Editar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(p.id)} disabled={busy === p.id}>
                          {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Excluir'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {partners.length === 0 && (
                  <tr>
                    <td colSpan="7" className="py-4 text-center text-sm text-muted-foreground">
                      Nenhum parceiro encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editing ? 'Editar Parceiro' : 'Novo Parceiro'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Nome da Empresa *</label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">NIF *</label>
                    <Input
                      value={formData.nif}
                      onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Nome do Contacto</label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email *</label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Telefone</label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Comissão (%) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.commission_percentage}
                      onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      required
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="active">Ativo</option>
                      <option value="suspended">Suspenso</option>
                      <option value="inactive">Inativo</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={busy === 'save'} className="flex-1">
                    {busy === 'save' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : editing ? 'Atualizar' : 'Criar'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
